import {
  BankFragment,
  ConversionPaymentRecordFragment,
  TransferInput,
  OperationFragment,
  OperationHistoryByCriteriaFragment,
  Status,
  SwiftPaymentRecordFragment,
  TransferPaymentRecordFragment,
  ConversionInput,
  SwiftInput,
  OperationTypeCategoryFragment,
  CountryFragment,
  CurrencyForPaymentFragment,
  BudgetClassificationCodeFragment,
  PaginatedOperationHistoryByCriteriaFragment,
  PaymentSubscriptionStateEnum,
  TransferTemplateFragment,
  OperationTypeCategory,
  SearchOperationHistoryInput,
  PageInput,
  CardTransferRecordFragment,
  BudgetPaymentRecord,
  CardTransferInput,
  RefundedQrOperationRecordFragment,
  Operations,
  CardTransferRko2CardRecordFragment,
  QrOperationFilterInput,
  QrTransferRecordFragment,
  QrTransferInput,
  PaginatedSalaryDetailFragment,
  SalaryInfoWithDetailFragment,
  SalaryRecordFragment,
  QrTransferType,
  PersonalQrOperationFilterInput,
} from '@/app/graphql/payment/sdk.payment';
import { BaseAccountFragment, CurrencyForAccountFragment } from '@/app/graphql/account/sdk.account';
import { User } from '@/app/stores/user/types';
import { AccountForPayment, AccountWithPaymentTypes, BaseRkoAndCardInfo, CardList } from '@/shared/services/account';
import type { HISTORY_FILTER_STATUS } from '@/app/stores/history-transaction/types';

export type TransferForm = Omit<
  TransferInput,
  | 'payerAccount'
  | 'payerBic'
  | 'recipientBic'
  | 'paymentCode'
  | 'operation'
  | 'currency'
  | 'recipientAccountCurrency'
  | 'departmentId'
> & {
  operation: InterBankTransferOperation | null;
  payerAccount: AccountWithPaymentTypes | null;
  payerBic: BankFragment | null;
  recipientBic: BankFragment | null | undefined;
  paymentCode: BudgetClassificationCodeFragment | null | undefined;
  currency: CurrencyForAccountFragment | null;
  recipientAccountCurrency: CurrencyForAccountFragment | null;
  departmentId: number | null;
  commission?: number;
};

export type InterBankFormInput = TransferForm;

export type IntraBankFormInput = Omit<TransferForm, 'operation'> & {
  operation: OperationFragment | null;
};

export type ConversionFormInput = Omit<
  ConversionInput,
  | 'payerAccount'
  | 'recipientAccount'
  | 'payerBic'
  | 'currency'
  | 'recipientAccountCurrency'
  | 'operation'
  | 'departmentId'
> & {
  payerAccount: AccountWithPaymentTypes | null;
  recipientAccount: BaseAccountFragment | null;
  payerBic: BankFragment | null;
  currency: CurrencyForAccountFragment | null;
  recipientAccountCurrency: CurrencyForAccountFragment | null;
  operation: OperationFragment | null;
  departmentId: number | null;
};

export type CardFormInput = Omit<CardTransferInput, 'payerAccountId'> & {
  payerAccount: AccountForPayment | null;
  recipientAccount: string;
  recipientAccountCurrency: CurrencyForAccountFragment | null;
  payerCard: CardList | null;
  recipientCard: CardList | null;
  operation: OperationFragment | null;
};

export type SwiftFormInput = Omit<
  SwiftInput,
  | 'payerAccount'
  | 'payerBic'
  | 'currency'
  | 'recipientBic'
  | 'categoryCode'
  | 'commissionAccount'
  | 'commissionCurrency'
  | 'country'
  | 'intermediaryBankBic'
  | 'exoticCurrency'
  | 'operation'
  | 'departmentId'
> & {
  payerAccount: AccountWithPaymentTypes | null;
  payerBic: BankFragment | null;
  currency: CurrencyForAccountFragment | null;
  recipientBic: BankFragment | null | undefined;
  categoryCode: OperationTypeCategoryFragment | null | undefined;
  commissionAccount: AccountWithPaymentTypes | null;
  commissionCurrency: CurrencyForAccountFragment | null;
  country: CountryFragment | null;
  intermediaryBankBic: BankFragment | null | undefined;
  exoticCurrency: CurrencyForPaymentFragment | null;
  operation: OperationFragment | null;
  departmentId: number | null;
};

export interface QrPaymentForm extends Omit<QrTransferInput, 'payerAccountId'> {
  payerAccount: BaseRkoAndCardInfo | null;
  transferType: QrTransferType | null;
  recipientName: string;
  qrAccount: string;
  allowEditSum: boolean;
  commission: number;
  commissionPercent: number;
}

export type PaymentForm = InterBankFormInput | IntraBankFormInput | SwiftFormInput | CardFormInput;

export type InterBankTransferOperation = OperationFragment & {
  label: string;
  info: string;
  disable: boolean;
};

export type IntraBankOperation = {
  foreign: OperationFragment;
  own: OperationFragment;
};

export type GroupForDateOperationHistory = {
  [keyof: string]: OperationHistory[];
};

export interface SearchPaymentByCriteria {
  amountPages: number;
  amountElements: number;
  currentPage: number;
  list: OperationHistory[];
}

// Интерфейс недостающих полей для QR операций. Чтобы общая логика не ломалась
export interface PaymentAdapterData {
  id: number;
  legalPartyId: number;
  createdDate: string;
  amount: number;
  operationCode: Operations;
  purpose: string;
  valueDate: string;
  createdBy: string;
  currencyCode: string;
  recipientAccount: string;
  payerName: string;
  payerAccount: string;
  statusCode: Status;
  transferNum: string;
  origin: string;
  selected: boolean;
}

export interface PaymentAdditionalData {
  paymentStatus: {
    label: string;
    classList: string;
  };
  paymentDate: {
    text: string;
    time: string;
  };
  recipientDetail: string;
  formattedAmount: string;
  paymentImg: string;
  paymentValueDate: string;
  paymentType: string;
  isRepeatable: boolean;
  isEditable: boolean;
  isSignable: boolean;
  isExportable: boolean;
  isOpenPdf: boolean;
  isRevokable: boolean;
  isRemovable: boolean;
  isSendForSign: boolean;
  isUpdateble: boolean;
  isRecall: boolean;
}

export type OperationHistory = OperationHistoryByCriteriaFragment & PaymentAdditionalData & { selected: boolean };

export type PaymentTransfer = TransferPaymentRecordFragment & PaymentAdditionalData;

export type PaymentTransferByInvoice = PaymentTransfer & {
  invoiceId: number;
};

export type PaymentRko2CardTransfer = CardTransferRko2CardRecordFragment & PaymentAdditionalData;

export type PaymentCardTransfer = CardTransferRecordFragment & PaymentAdditionalData;

export type PaymentConversion = ConversionPaymentRecordFragment & PaymentAdditionalData;

export type PaymentSwift = SwiftPaymentRecordFragment & PaymentAdditionalData;

export type PaymentBudget = BudgetPaymentRecord & PaymentAdditionalData;

export type PaymentQrTransfer = QrTransferRecordFragment & PaymentAdditionalData & PaymentAdapterData;

export type PaymentQrReturned = RefundedQrOperationRecordFragment & PaymentAdditionalData & PaymentAdapterData;

export interface PaymentOptions {
  recipientBank?: BankFragment | null;
  intermediaryBank?: BankFragment | null;
  budgetClassification?: BudgetClassificationCodeFragment | null;
  operationCategory?: OperationTypeCategory | null;
}

export type RepeatablePaymentTransfer = TransferPaymentRecordFragment & PaymentOptions;
export type RepeatablePaymentSwift = SwiftPaymentRecordFragment & PaymentOptions;
export type RepeatablePaymentRko2Card = CardTransferRecordFragment & PaymentOptions;
export type PaymentTemplate = TransferTemplateFragment & PaymentOptions;

export interface PaymentStatusHistoryInfo {
  label: string;
  updatedBy: string;
  id?: number;
  color?: string;
  isSigned?: boolean;
  statusCode?: Status;
  updatedDate?: string;
}

export enum OperationTypes {
  CLEARING = 'CLEARING',
  GROSS = 'GROSS',
  SWIFT = 'SWIFT',
  INTRABANK_TRANSFER_OWN = 'INTRABANK_TRANSFER_OWN',
  INTRABANK_TRANSFER_FOREIGN = 'INTRABANK_TRANSFER_FOREIGN',
  RKO_2_BUSINESS_CARD_FOREIGN = 'RKO_2_BUSINESS_CARD_FOREIGN',
  RKO_2_BUSINESS_CARD_OWN = 'RKO_2_BUSINESS_CARD_OWN',
  RKO_2_INDIVIDUAL_CARD_FOREIGN = 'RKO_2_INDIVIDUAL_CARD_FOREIGN',
  RKO_2_INDIVIDUAL_CARD_OWN = 'RKO_2_INDIVIDUAL_CARD_OWN',
  BUSINESS_CARD_2_RKO_OWN = 'BUSINESS_CARD_2_RKO_OWN',
  BUSINESS_CARD_2_RKO_FOREIGN = 'BUSINESS_CARD_2_RKO_FOREIGN',
  CONVERSION = 'CONVERSION',
  BUDGET_PAYMENT = 'BUDGET_PAYMENT',
}

export enum CardTransferTypes {
  BUSINESS_CARD = 'business-card', // перевод на бизнес карту
  O24 = 'o24', // перевод на о24
  RKO = 'rko', // перевод с бизнес карты на расчетный счет
}

export enum Countries {
  KGS = 248,
  RUS = '1100', // Россия
  BLR = '1500', // Беларусь
}

export interface ListenPayment {
  data: {
    listenPayment: {
      paymentInformation: OperationHistoryByCriteriaFragment;
      state: PaymentSubscriptionStateEnum;
    };
  };
}

export interface ListenPaymentHistory {
  paymentInformation: OperationHistoryByCriteriaFragment;
  state: PaymentSubscriptionStateEnum;
}

export interface PaymentByCriteriaParams {
  payments: PaginatedOperationHistoryByCriteriaFragment;
  user: User;
}

export interface SearchPaymentByCriteriaParams {
  searchOperationHistory: SearchOperationHistoryInput;
  pageable: PageInput;
  user: User;
  transactions?: OperationHistory[];
}

export enum TYPES_EXCHANGE_RATES {
  CASH = 'cash',
  CASHLESS = 'cashless',
  CARD = 'card',
  NBKR = 'nbkr',
}

export enum TYPES_OPERATION_EXCHANGE_RATES {
  PURCHASE = 'purchase',
  SALE = 'sale',
}

type QrOperationHistoryFilterInput = Omit<QrOperationFilterInput, 'isOnlySalePointOperations'> & {
  isOnlySalePointOperations?: boolean;
};

export interface QrHistoryOperationFilter extends Omit<QrOperationHistoryFilterInput, 'legalPartyId'> {
  legalPartyId: number | undefined;
}

export interface QrHistoryOutletOperationFilter
  extends Omit<QrOperationHistoryFilterInput, 'legalPartyId' | 'qrAccount' | 'salePointCodeList'> {
  legalPartyId: number | undefined;
  salePointCode: number | null;
  cashCodes: number[];
  amountElements: number | undefined;
  currentPage: number | undefined;
}

export interface SalaryStatement extends SalaryRecordFragment {
  statusLabel?: {
    label: string;
    classList: string;
  };
  index?: number;
  isSendForSign?: boolean;
  isSignable?: boolean;
  isRevokable?: boolean;
  isRemovable?: boolean;
  isRetry?: boolean;
  isOpen?: boolean;
  isDownloadPdf?: boolean;
  isRepeatStatement?: boolean;
  hasAction?: boolean;
  status: Status;
}

export interface PaginatedSalaryStatement {
  list: SalaryStatement[];
  amountPages: number;
  amountElements: number;
}

export interface ShortInfoSalaryStatement {
  salaryId: number;
  paymentId: number;
  fileName: string;
  totalAmount: number;
  valueDate: string;
  account: string;
  fileId: number | null;
  creatorName: string;
  createdDate: string;
  companyName: string;
  currency: {
    isoCode: string;
  };
  statusLabel?: {
    label: string;
    classList: string;
  };
  isSendForSign?: boolean;
  isSignable?: boolean;
  isRevokable?: boolean;
  isRemovable?: boolean;
  isDownloadPdf?: boolean;
  isRepeatStatement?: boolean;
  status: Status;
}

export interface PaginatedSalaryStatementDetail extends PaginatedSalaryDetailFragment {
  amountPages: number;
  amountElements: number;
  list: {
    id: number;
    fullName: string;
    cardAccount: string;
    amount: number;
    state: boolean | null;
    index?: number;
  }[];
}

export interface SalaryInfoWithDetail extends SalaryInfoWithDetailFragment {
  statusLabel?: {
    label: string;
    classList: string;
  };
  status: Status;
  isSendForSign: boolean;
  isSignable: boolean;
  isRevokable: boolean;
  isRemovable: boolean;
  isDownloadPdf?: boolean;
  isRepeatStatement?: boolean;
}

export interface FilterSalaryStatement {
  endDate: string | null;
  startDate: string | null;
  status: HISTORY_FILTER_STATUS;
}

export interface ListenSalaryStatement {
  data: {
    listenSalary: {
      salaryResponseDto: SalaryStatement;
      state: PaymentSubscriptionStateEnum;
    };
  };
}

export interface PersonalQrHistoryOperationFilter
  extends Omit<PersonalQrOperationFilterInput, 'legalPartyId' | 'qrTransactionId' | 'startDate' | 'endDate'> {
  legalPartyId: number | null;
  qrTransactionId: string | null;
  startDate: string | null;
  endDate: string | null;
}
